import { useTheme } from "@mui/material/styles";
import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import { FC, useMemo } from "react";
import { useTillSummary } from "~/hooks/use-till-summary";
import { $dayjs, DATESTRING_FORMAT, DATEYEARMONTH_FORMAT } from "~/libs/dayjs";
import { GetCurrentCompanyResults } from "~/queries/useGetCurrentCompany";
import { GetFileByIdResults } from "~/queries/useGetFileById";
import { GetProductOptionsResults } from "~/queries/useGetProductOptions";
import { GetProductsResults } from "~/queries/useGetProducts";
import { CUSTOM_FIELD_TYPE } from "~/types/__generated/gql/graphql";
const useStyles = () => {
  const theme = useTheme();
  return useMemo(() => {
    return StyleSheet.create({
      page: {
        fontFamily: "Noto Serif SC",
        backgroundColor: "#FFFFFF",
        padding: 24
      },
      h4: {
        fontSize: 12,
        fontWeight: "bold",
        lineHeight: 1.235
      },
      h6: {
        fontSize: 10,
        fontWeight: 800,
        lineHeight: 1.6
      },
      subtitle2: {
        fontSize: 8.5,
        fontWeight: 500,
        lineHeight: 1.57
      },
      body2: {
        fontSize: 8.5,
        fontWeight: 400,
        lineHeight: 1.43
      },
      gutterBottom: {
        marginBottom: 4
      },
      colorSuccess: {
        color: theme.palette.success.main
      },
      colorError: {
        color: theme.palette.error.main
      },
      uppercase: {
        textTransform: "uppercase"
      },
      headerContainer: {
        flexDirection: "row",
        justifyContent: "space-between"
      },
      headerContent: {
        flex: 1
      },
      headerTextCenter: {
        textAlign: "center"
      },
      companies: {
        marginTop: 32,
        borderBottomWidth: 1,
        borderColor: "#d9d9d9",
        borderStyle: "solid"
      },
      companyRow: {
        flexDirection: "row"
      },
      companyColLeft: {
        padding: 6,
        width: "50%",
        border: "1px",
        borderBottom: "1px",
        borderColor: "#d9d9d9",
        borderStyle: "solid"
      },
      companyColRight: {
        padding: 6,
        width: "50%",
        border: "1px",
        borderLeft: "1px",
        borderBottom: "1px",
        borderColor: "#d9d9d9",
        borderStyle: "solid"
      },
      items: {
        marginTop: 36,
        borderBottomWidth: 1,
        borderColor: "#d9d9d9",
        borderStyle: "solid"
      },
      itemRow: {
        flexDirection: "row"
      },
      itemNumber: {
        border: "1px",
        borderBottom: "1px",
        borderColor: "#d9d9d9",
        borderStyle: "solid",
        padding: 6,
        width: "10%"
      },
      itemDescription: {
        border: "1px",
        borderBottom: "1px",
        borderLeft: "1px",
        borderColor: "#d9d9d9",
        borderStyle: "solid",
        padding: 6,
        width: "70%"
      },
      itemQty: {
        border: "1px",
        borderBottom: "1px",
        borderLeft: "1px",
        borderColor: "#d9d9d9",
        borderStyle: "solid",
        padding: 6,
        width: "20%"
      },
      notes: {
        marginTop: 36
      },
      noteHeader: {
        textAlign: "center",
        justifyContent: "center"
      },
      noteRow: {
        border: "1px",
        borderColor: "#d9d9d9",
        borderStyle: "solid",
        flexDirection: "row",
        padding: 6
      }
    });
  }, [theme]);
};
export const OrderSheetDocument: FC<{
  file: GetFileByIdResults;
  company: GetCurrentCompanyResults;
  itemsSummary: ReturnType<typeof useTillSummary>["updateFileInputSummary"];
  products: GetProductsResults;
  productOptions: GetProductOptionsResults;
}> = ({
  file,
  company,
  itemsSummary,
  products,
  productOptions
}) => {
  const styles = useStyles();
  const notes = file.customFields.find(f => (f._customField.name.toLowerCase() === "note" || f._customField.name.toLowerCase() === "notes") && (f._customField.type === CUSTOM_FIELD_TYPE.TEXT_SINGLE || f._customField.type === CUSTOM_FIELD_TYPE.TEXT_MULTI));
  const deliveryDate = file.customFields.find(f => (f._customField.name.toLowerCase() === "due date" || f._customField.name.toLowerCase() === "delivery date") && f._customField.type === CUSTOM_FIELD_TYPE.DATETIME);
  return <Document data-sentry-element="Document" data-sentry-component="OrderSheetDocument" data-sentry-source-file="order-sheet-document.tsx">
			<Page size="A4" style={styles.page} data-sentry-element="Page" data-sentry-source-file="order-sheet-document.tsx">
				<View style={styles.headerContainer} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
					<View style={styles.headerContent} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
						<Text style={styles.h6} data-sentry-element="Text" data-sentry-source-file="order-sheet-document.tsx">{company.name}</Text>
					</View>
				</View>
				<View style={styles.headerContainer} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
					<View style={styles.headerContent} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
						{/* {file.counterConfigs?.file?.reference &&
       file.counterConfigs?.file?.reference !== "" ? (
       <Text style={styles.h6}>
       	{file.counterConfigs?.file?.reference}
       </Text>
       ) : ( */}
						<Text style={styles.h6} data-sentry-element="Text" data-sentry-source-file="order-sheet-document.tsx">
							{file.counterConfigs?.file?._counter.isPredefinedFormat ? `${file.counterConfigs?.file?._counter.prefix ? `${file.counterConfigs?.file?._counter.prefix}-` : ""}${$dayjs(file.createdAt).format(DATEYEARMONTH_FORMAT)}` : file.counterConfigs?.file?._counter.prefix}
							{"-"}
							{file?.counterConfigs?.file?.index ? String(file?.counterConfigs?.file?.index || 0).padStart(file?.counterConfigs?.file?._counter?.suffix_digit || 0, "0") : ""}
							{file.fileSuffixComment ? `-${file.fileSuffixComment}` : ""}
						</Text>
						{/* )} */}
					</View>
					<View style={styles.headerContent} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
						<Text style={[styles.h4, styles.headerTextCenter]} data-sentry-element="Text" data-sentry-source-file="order-sheet-document.tsx">
							ORDER SHEET
						</Text>
					</View>
					<View style={styles.headerContent} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx"></View>
				</View>
				<View style={styles.companies} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
					<View style={styles.companyRow} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
						<View style={styles.companyColLeft} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
							<Text style={styles.body2} data-sentry-element="Text" data-sentry-source-file="order-sheet-document.tsx">
								Client: {file._client?.companyIssuedId ?? ""} -{" "}
								{file._client?.name ?? ""}
							</Text>
						</View>
						<View style={styles.companyColRight} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
							<Text style={styles.body2} data-sentry-element="Text" data-sentry-source-file="order-sheet-document.tsx">
								Order Creator: {file._user.firstName} {file._user.lastName}
							</Text>
						</View>
					</View>

					<View style={styles.companyRow} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
						<View style={styles.companyColLeft} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
							{company.isDisplayClientInOrderSheet ? <Text style={styles.body2}>
									Client Contact:{" "}
									{`${file._client?.contacts?.[0]?.firstName ?? ""} ${file._client?.contacts?.[0]?.lastName ?? ""}`}
								</Text> : <Text style={styles.body2}>
									Agent: {file._agent?.companyIssuedId ?? ""} -{" "}
									{file._agent?.name ?? ""}
								</Text>}
						</View>
						<View style={styles.companyColRight} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
							<Text style={styles.body2} data-sentry-element="Text" data-sentry-source-file="order-sheet-document.tsx">
								Created At: {$dayjs(file.createdAt).format(DATESTRING_FORMAT)}
							</Text>
						</View>
					</View>

					<View style={styles.companyRow} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
						<View style={styles.companyColLeft} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
							{company.isDisplayClientInOrderSheet ? <Text style={styles.body2}>
									Client Email: {file._client?.contacts?.[0]?.email ?? ""}
								</Text> : <Text style={styles.body2}>
									Agent Contact:{" "}
									{`${file._agent?.contacts[0].firstName ?? ""} ${file._agent?.contacts[0].lastName ?? ""}`}
								</Text>}
						</View>
						<View style={styles.companyColRight} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
							<Text style={styles.body2} data-sentry-element="Text" data-sentry-source-file="order-sheet-document.tsx">
								Assignees:{" "}
								{file._assignees ? file._assignees.map(a => `${a.firstName} ${a.lastName}`).join(", ") : ""}
							</Text>
						</View>
					</View>

					<View style={styles.companyRow} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
						<View style={styles.companyColLeft} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
							{company.isDisplayClientInOrderSheet ? <Text style={styles.body2}>
									Client Mobile: {file._client?.contacts?.[0]?.mobile ?? ""}
								</Text> : <Text style={styles.body2}>
									Agent Email: {file._agent?.contacts[0].email ?? ""}
								</Text>}
						</View>
						<View style={styles.companyColRight} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
							<Text style={styles.body2} data-sentry-element="Text" data-sentry-source-file="order-sheet-document.tsx">
								Due Date:{" "}
								{deliveryDate ? $dayjs(deliveryDate.dateTimeValue).format(DATESTRING_FORMAT) : ""}
							</Text>
						</View>
					</View>

					<View style={styles.companyRow} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
						<View style={styles.companyColLeft} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
							{company.isDisplayClientInOrderSheet ? <Text style={styles.body2}>
									Client Adress: {file._client?._company?.address ?? ""}
								</Text> : <Text style={styles.body2}>
									Agent Mobile: {file._agent?.contacts[0].mobile ?? ""}
								</Text>}
						</View>
						<View style={styles.companyColRight} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx"></View>
					</View>

					{!company.isDisplayClientInOrderSheet && <View style={styles.companyRow}>
							<View style={styles.companyColLeft}>
								<Text style={styles.body2}>
									Agent Adress: {file._agent?._company.address ?? ""}
								</Text>
							</View>
							<View style={styles.companyColRight}></View>
						</View>}
				</View>

				<View style={styles.items} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
					<View style={styles.itemRow} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
						<View style={styles.itemNumber} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
							<Text style={styles.h6} data-sentry-element="Text" data-sentry-source-file="order-sheet-document.tsx">#</Text>
						</View>
						<View style={styles.itemDescription} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
							<Text style={styles.h6} data-sentry-element="Text" data-sentry-source-file="order-sheet-document.tsx">Product</Text>
						</View>
						<View style={styles.itemQty} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
							<Text style={styles.h6} data-sentry-element="Text" data-sentry-source-file="order-sheet-document.tsx">Qty</Text>
						</View>
					</View>
					{itemsSummary.products.map((item, index) => {
          return <View key={item._id.toString()} style={styles.itemRow}>
								<View style={styles.itemNumber}>
									<Text style={styles.body2}>{index + 1}</Text>
								</View>
								<View style={styles.itemDescription}>
									<Text style={styles.body2}>
										{products.find(p => p._id === item.product)?.name ?? "[UNKNOWN PRODUCT]"}
									</Text>
									<Text style={styles.body2}>
										{item.options.map(option => productOptions.find(p => p._id === option.option)?.name ?? "[UNKNOWN PRODUCT OPTION]").join(", ")}
									</Text>
								</View>
								<View style={styles.itemQty}>
									<Text style={styles.body2}>{item.quantity}</Text>
								</View>
							</View>;
        })}
				</View>
				<View style={styles.notes} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
					<View style={[styles.noteRow, styles.noteHeader]} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
						<Text style={styles.body2} data-sentry-element="Text" data-sentry-source-file="order-sheet-document.tsx">Notes</Text>
					</View>
					<View style={styles.noteRow} data-sentry-element="View" data-sentry-source-file="order-sheet-document.tsx">
						<Text style={styles.body2} data-sentry-element="Text" data-sentry-source-file="order-sheet-document.tsx">{notes ? notes.textValue : ""}</Text>
					</View>
				</View>
			</Page>
		</Document>;
};